// View Bill Modal Component
// Integrated with real API data

import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../redux/hooks";

// Mui
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

// Utils
import { fetchGETRequest, fetchPUTRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";
import useMeterDeviceEvents from "../../hooks/useMeterDeviceEvents";

interface ViewBillModalProps {
  open: boolean;
  onClose: () => void;
  consumerId?: string;
  billingDates?: {
    startDate: Date;
    endDate: Date;
  };
}

interface ConsumerData {
  id?: { id: string };
  // Top-level fields from API response
  phone?: string;
  city?: string;
  state?: string;
  country?: string;
  additionalInfo?: {
    firstName?: string;
    lastName?: string;
    salutation?: string;
    consumerType?: string;
    consumerBillingType?: string;
    mobileNumber?: string;
    emailId?: string;
    contacts?: Array<{ type: string; value: string }>;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    streetAddress?: string;
    houseNo?: string;
    // Site IDs for fetching address from asset
    siteIds?: string[];
    siteId?: string;
  };
}

interface DeviceData {
  id: { id: string };
  name: string;
  additionalInfo?: {
    meterCommodityType?: string;
    status?: string;
  };
}

interface BillingReadings {
  min: number;
  max: number;
  unitsConsumed: number;
}

interface AssetData {
  id: { id: string };
  type: string;
  additionalInfo?: {
    houseNo?: string;
    streetAddress?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

const ViewBillModal: React.FC<ViewBillModalProps> = ({
  open,
  onClose,
  consumerId,
  billingDates,
}) => {
  const [loading, setLoading] = useState(false);
  const [consumerData, setConsumerData] = useState<ConsumerData | null>(null);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [assetData, setAssetData] = useState<AssetData | null>(null);
  const [billingReadings, setBillingReadings] =
    useState<BillingReadings | null>(null);
  const [tariffRate, setTariffRate] = useState<number>(6.2); // Default fallback

  const { selectedMeter } = useAppSelector((state) => state.billing);

  // Constants (can be made configurable later)
  const FIXED_CHARGE_RATE = 20.0; // ₹/kW
  const SANCTIONED_LOAD = 6; // kW (default, can come from asset data)
  const ELECTRICITY_DUTY_RATE = 0.0302; // 3.02%

  useEffect(() => {
    if (open && consumerId) {
      fetchData();
    }
  }, [open, consumerId]);

  const fetchData = async () => {
    if (!consumerId) return;

    setLoading(true);
    setTariffRate(6.2);
    setBillingReadings(null);
    try {
      // Fetch consumer data
      const response = await fetchGETRequest(`v2/Customers/${consumerId}`);
      const consumerResponse = response?.data;
      setConsumerData(consumerResponse);

      // Get siteId from consumer additionalInfo to fetch address
      const siteIds = consumerResponse?.additionalInfo?.siteIds;
      const siteId = consumerResponse?.additionalInfo?.siteId;

      // Determine the siteId to use (first from array or single siteId)
      const assetId = siteIds && siteIds.length > 0 ? siteIds[0] : siteId;

      // Fetch asset by siteId to get address info
      if (assetId) {
        const assetResponse: any = await fetchGETRequest(
          `asset/${assetId}`,
          {},
          true,
        );
        // Handle both response formats (with and without sendResponse=true)
        if (assetResponse?.data?.additionalInfo) {
          setAssetData(assetResponse.data as AssetData);
        } else if (assetResponse?.additionalInfo) {
          setAssetData(assetResponse as AssetData);
        }
      }

      // Fetch devices for the consumer
      const devicesResponse = await fetchGETRequest(
        `v2/Meters/by-customer/${consumerId}`,
      );

      const devicesList = devicesResponse?.data || [];
      setDevices(devicesList);

      // Fetch billing determinants for the selected meter or the first meter
      if (
        devicesList.length > 0 &&
        billingDates &&
        billingDates.startDate &&
        billingDates.endDate
      ) {
        const targetDeviceId = selectedMeter || devicesList[0]?.id?.id;
        const startTs = billingDates.startDate.getTime();
        const endTs = billingDates.endDate.getTime();

        const billingResponse = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${targetDeviceId}/getBillingDeterminants?keys=IP_KWH,EventCode&startTs=${startTs}&endTs=${endTs}&intervalType=WEEK`,
        );

        if (billingResponse?.IP_KWH && billingResponse.IP_KWH.length > 0) {
          const values = billingResponse.IP_KWH.map((item: { value: string }) =>
            parseFloat(item.value || "0"),
          );
          const min = Math.min(...values);
          const max = Math.max(...values);
          setBillingReadings({
            min,
            max,
            unitsConsumed: max - min,
          });
        } else {
          setBillingReadings(null);
        }

        // Fetch current tariff for the selected meter
        try {
          const tariffBody = {
            command: "GetCurrentMeterTariff",
            deviceIds: [targetDeviceId],
            projectName: "",
          };
          const tariffResponse: any = await fetchPUTRequest(
            `devices/command`,
            tariffBody,
          );
          const dynamicTariff =
            tariffResponse?.TariffAmount ||
            tariffResponse?.value ||
            tariffResponse?.result ||
            tariffResponse?.data ||
            tariffResponse?.response ||
            null;

          if (dynamicTariff !== null && !isNaN(parseFloat(dynamicTariff))) {
            setTariffRate(parseFloat(dynamicTariff));
          }
        } catch (tariffError) {
          console.error("Error fetching tariff:", tariffError);
        }
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build consumer details from API data
  const getConsumerName = () => {
    const info = consumerData?.additionalInfo;
    if (info?.firstName || info?.lastName) {
      return `${info?.salutation || ""} ${info?.firstName || ""} ${info?.lastName || ""}`.trim();
    }
    return "-";
  };

  const getConsumerAddress = () => {
    // First try to get address from top-level consumer fields (city, state, country)
    if (consumerData?.city || consumerData?.state || consumerData?.country) {
      const parts = [
        consumerData.city,
        consumerData.state,
        consumerData.country,
      ].filter(Boolean);
      if (parts.length > 0) {
        return parts.join(", ");
      }
    }

    // Then try to get address from asset (LOCATION type)
    const assetInfo = assetData?.additionalInfo;
    if (assetInfo) {
      const parts = [
        assetInfo.houseNo,
        assetInfo.streetAddress,
        assetInfo.city,
        assetInfo.state,
        assetInfo.zip,
      ].filter(Boolean);
      if (parts.length > 0) {
        return parts.join(", ");
      }
    }

    // Fallback to consumer additionalInfo
    const info = consumerData?.additionalInfo;
    const parts = [
      info?.houseNo,
      info?.streetAddress,
      info?.city,
      info?.state,
      info?.zip,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : info?.address || "-";
  };

  const getMobileNumber = () => {
    // First check top-level phone field
    if (consumerData?.phone) return consumerData.phone;

    const info = consumerData?.additionalInfo;
    // Check for mobileNumber field in additionalInfo
    if (info?.mobileNumber) return info.mobileNumber;
    // Then check contacts array
    if (
      info?.contacts &&
      Array.isArray(info.contacts) &&
      info.contacts.length > 0
    ) {
      // Look for mobile or phone type contact
      const mobile = info.contacts.find(
        (c) =>
          c.type?.toLowerCase() === "mobile" ||
          c.type?.toLowerCase() === "phone",
      );
      if (mobile?.value) return mobile.value;
      // Return first contact value as fallback
      return info.contacts[0]?.value || "-";
    }
    return "-";
  };

  const getMeterType = () => {
    const billingType = consumerData?.additionalInfo?.consumerBillingType;
    if (billingType?.toLowerCase().includes("prepaid")) {
      return "Prepaid";
    }
    return "Postpaid";
  };

  // Calculate billing values
  const unitsConsumed = billingReadings?.unitsConsumed || 0;
  const billingDays =
    billingDates && billingDates.startDate && billingDates.endDate
      ? Math.ceil(
          (billingDates.endDate.getTime() - billingDates.startDate.getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : 30;
  const avgDailyUse =
    billingDays > 0 ? (unitsConsumed / billingDays).toFixed(1) : "0";

  // Fetch meter events for penalty calculation
  const { data: meterEvents = [] } = useMeterDeviceEvents(selectedMeter || "");

  // Calculate penalty (₹ 50 per METER_COVER_OPEN event)
  const penaltyCount = meterEvents.filter((event: any) => {
    if (!billingDates?.startDate || !billingDates?.endDate) return false;
    const eventTs = new Date(event.CreatedAt).getTime();
    return (
      event.Command === "METER_COVER_OPEN" &&
      eventTs >= billingDates.startDate.getTime() &&
      eventTs <= billingDates.endDate.getTime()
    );
  }).length;

  const penaltyAmount = penaltyCount * 50;

  // Calculate charges
  const energyCharge = unitsConsumed * tariffRate;
  const fixedCharge = SANCTIONED_LOAD * FIXED_CHARGE_RATE;
  const totalEnergyCharges = energyCharge + fixedCharge;
  const electricityDuty = energyCharge * ELECTRICITY_DUTY_RATE;
  const totalTaxes = electricityDuty + penaltyAmount;
  const totalDue = Math.round(totalEnergyCharges + totalTaxes);

  // Energy charges data (only Energy Charge and Fixed Charges)
  const energyChargesData = [
    {
      description: "Energy Charge",
      unitsBasis: `${unitsConsumed.toFixed(2)} kWh`,
      rate: `₹ ${tariffRate.toFixed(2)} / kWh`,
      amount: energyCharge,
    },
    {
      description: "Fixed Charge",
      unitsBasis: `${SANCTIONED_LOAD} kW`,
      rate: `₹ ${FIXED_CHARGE_RATE.toFixed(2)} / kW`,
      amount: fixedCharge,
    },
  ];

  // Taxes data (Electricity Duty and Penalty)
  const taxesData = [
    {
      description: "Electricity Duty",
      basis: "On Energy Charge",
      rate: `${(ELECTRICITY_DUTY_RATE * 100).toFixed(2)}%`,
      amount: electricityDuty,
    },
    ...(penaltyAmount > 0
      ? [
          {
            description: "Penalty (Tamper Event)",
            basis: `${penaltyCount} Events`,
            rate: "₹ 50.00 / Event",
            amount: penaltyAmount,
          },
        ]
      : []),
  ];

  // Summary values (all 0 as requested)
  const summaryData = {
    previousBalance: 0,
    governmentSubsidy: 0,
    latePaymentCharges: 0,
    carryForward: 0,
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #1064ED 0%, #1064ED 100%)",
          color: "#fff",
          py: 2,
          px: 3,
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
            >
              Consumer Billing Statement
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, color: "#fff" }}>
              Monthly Cycle
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={2}>
            <Chip
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 10,
                    color: getMeterType() === "Prepaid" ? "#ff9800" : "#4caf50",
                  }}
                />
              }
              label={`Smart Meter – ${getMeterType()}`}
              size="small"
              sx={{
                backgroundColor:
                  getMeterType() === "Prepaid"
                    ? "rgba(255, 152, 0, 0.15)"
                    : "rgba(76, 175, 80, 0.15)",
                color: getMeterType() === "Prepaid" ? "#ffcc80" : "#c8e6c9",
                border:
                  getMeterType() === "Prepaid"
                    ? "1px solid rgba(255, 152, 0, 0.5)"
                    : "1px solid rgba(76, 175, 80, 0.5)",
                "& .MuiChip-icon": {
                  color: getMeterType() === "Prepaid" ? "#ff9800" : "#4caf50",
                },
              }}
            />
            <IconButton onClick={onClose} sx={{ color: "#fff" }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Two Column Layout - Consumer & Billing Details */}
            <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
              {/* Consumer Details Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    height: "100%",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Consumer Details
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {consumerData?.additionalInfo?.consumerType ||
                          "Urban Domestic – LT"}
                      </Typography>
                    </Box>
                    <Chip
                      label={`Sanctioned Load: ${SANCTIONED_LOAD} kW`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Consumer Name
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {getConsumerName()}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Consumer No.
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {consumerData?.id?.id || consumerId || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Address
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {getConsumerAddress()}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Mobile No.
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {getMobileNumber()}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Category
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {consumerData?.additionalInfo?.consumerType ||
                          "Domestic – Residential"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Billing Details Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    height: "100%",
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={1}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Billing Details
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Bill Month:{" "}
                        {billingDates
                          ? formatDate(billingDates.startDate, "MMMM YYYY")
                          : "-"}
                      </Typography>
                    </Box>
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        backgroundColor: "#fff3e0",
                        color: "#e65100",
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                  <Grid container spacing={1}>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Bill Date
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(new Date(), "DD MMM YYYY")}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Due Date
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {formatDate(
                          new Date(Date.now() + 15 * 24 * 60 * 60 * 1000),
                          "DD MMM YYYY",
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Billing Period
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {billingDates
                          ? `${formatDate(billingDates.startDate, "DD MMM YYYY")} – ${formatDate(billingDates.endDate, "DD MMM YYYY")}`
                          : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Payment Status
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        Unpaid
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Bill Type
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        Actual (Smart Meter)
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>

            {/* Meter & Consumption + Summary */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Meter & Consumption Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}
                >
                  <Box mb={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Meter & Consumption
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Smart Meter – {getMeterType()}
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Meter No.
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {selectedMeter
                          ? devices.find((d) => d.id.id === selectedMeter)
                              ?.name || selectedMeter
                          : devices.length > 0
                            ? devices[0]?.name
                            : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Previous Reading
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {billingReadings ? billingReadings.min.toFixed(2) : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Current Reading
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {billingReadings ? billingReadings.max.toFixed(2) : "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Units Consumed
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary"
                      >
                        {unitsConsumed.toFixed(2)} kWh
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Avg Daily Use
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        {avgDailyUse} kWh/day
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography variant="caption" color="text.secondary">
                        Tariff Rate
                      </Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="body2" fontWeight={500}>
                        ₹ {tariffRate.toFixed(2)} / kWh
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Summary Card */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box mb={1}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Previous Balance & Subsidy
                    </Typography>
                  </Box>

                  <Stack spacing={1} sx={{ flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Previous Balance (Arrears)
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        ₹ {summaryData.previousBalance.toFixed(2)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Government Subsidy
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        ₹ {summaryData.governmentSubsidy.toFixed(2)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Late Payment Charges
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        ₹ {summaryData.latePaymentCharges.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Carry Forward at bottom */}
                  <Box sx={{ mt: "auto" }}>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" fontWeight={600}>
                        Carry Forward Amount
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ₹ {summaryData.carryForward.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Charges & Duties Table */}
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2, border: "1px solid #e0e0e0" }}
            >
              <Box mb={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  Energy Charges & Duties
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Applicable as per approved tariff
                </Typography>
              </Box>

              {/* Energy Charges Table */}
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "block",
                  mb: 1,
                }}
              >
                Energy Charges
              </Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Units / Basis
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Rate
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Amount (₹)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {energyChargesData.map((charge, index) => (
                      <TableRow key={index}>
                        <TableCell>{charge.description}</TableCell>
                        <TableCell align="right">{charge.unitsBasis}</TableCell>
                        <TableCell align="right">{charge.rate}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: index === 0 ? 700 : 500,
                            color: index === 0 ? "text.primary" : "inherit",
                          }}
                        >
                          ₹ {charge.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Taxes & Duties Table */}
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "block",
                  mb: 1,
                }}
              >
                Taxes & Duties
              </Typography>
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Description
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Basis
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Rate
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Amount (₹)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taxesData.map((tax, index) => (
                      <TableRow key={index}>
                        <TableCell>{tax.description}</TableCell>
                        <TableCell align="right">{tax.basis}</TableCell>
                        <TableCell align="right">{tax.rate}</TableCell>
                        <TableCell align="right">
                          ₹ {tax.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Bill Total Section */}
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "block",
                  mb: 1,
                }}
              >
                Bill Total
              </Typography>
              <Stack spacing={0.5} sx={{ maxWidth: 400, ml: "auto" }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total Energy & Other Charges
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ₹ {totalEnergyCharges.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total Taxes & Duties
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ₹ {totalTaxes.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>
                    Add: Carry Forward Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    ₹ {summaryData.carryForward.toFixed(2)}
                  </Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body1" fontWeight={700}>
                    Total Amount Due (Rounded)
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    color="warning.dark"
                  >
                    ₹ {totalDue.toFixed(2)}
                  </Typography>
                </Stack>
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 2 }}
              >
                *Rounded off as per billing rules. This bill is generated based
                on smart meter readings received through AMI and approved tariff
                order.
              </Typography>
            </Paper>
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid #e0e0e0",
          justifyContent: "space-between",
          backgroundColor: "#fff",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Consumer No.:{" "}
          <strong>{consumerData?.id?.id || consumerId || "-"}</strong>
        </Typography>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ViewBillModal;
